import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { revokeToolAccess, type Member } from "@/lib/api";

type GetToken = () => Promise<string | null>;

interface RevokeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: Member | null;
  productId: string;
  orgId: string;
  onSuccess: () => void;
  getToken: GetToken;
}

export function RevokeDialog({
  open,
  onOpenChange,
  member,
  productId,
  orgId,
  onSuccess,
  getToken,
}: RevokeDialogProps) {
  const [working, setWorking] = useState(false);

  async function onRevoke() {
    if (!member) return;
    setWorking(true);
    try {
      await revokeToolAccess(getToken, {
        clerkUserId: member.clerkUserId,
        productId,
      }, orgId);
      toast.success(
        `Revoked tool access for ${member.displayName ?? member.email ?? "user"}`
      );
      onSuccess();
      onOpenChange(false);
    } catch {
      toast.error("Failed to revoke access");
    } finally {
      setWorking(false);
    }
  }

  const name = member?.displayName ?? member?.email ?? "this member";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Revoke tool access</DialogTitle>
          <DialogDescription>
            This will remove the explicit tool role for {name}. They may still
            have access through org defaults or group membership.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={working}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={onRevoke} disabled={working}>
            {working ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Revoke
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
